"use client";

import { c as _c } from "react/compiler-runtime";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "@/components/auth/Auth";
import MyStreamWaveCurationCreateDialog from "@/components/brain/my-stream/tabs/MyStreamWaveCurationCreateDialog";
import MobileWrapperDialog from "@/components/mobile-wrapper-dialog/MobileWrapperDialog";
import { Spinner } from "@/components/dotLoader/DotLoader";
import PrimaryButton from "@/components/utils/button/PrimaryButton";
import SecondaryButton from "@/components/utils/button/SecondaryButton";
import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { type DropCurationMembership, useDropCurations } from "@/hooks/drops/useDropCurations";
import { useDropCurationMembershipMutation } from "@/hooks/drops/useDropCurationMembershipMutation";
import type { ApiWaveCuration } from "@/generated/models/ApiWaveCuration";
import type { ApiWaveMin } from "@/generated/models/ApiWaveMin";
const getErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return fallback;
};
const isPermissionErrorMessage = (message: string): boolean => {
  const normalizedMessage = message.toLowerCase();
  return normalizedMessage.includes("permission") || normalizedMessage.includes("forbidden") || normalizedMessage.includes("not authorized") || normalizedMessage.includes("not allowed") || normalizedMessage.includes("cannot curate") || normalizedMessage.includes("can't curate") || normalizedMessage.includes("not a member");
};
const CREATE_AND_ADD_PERMISSION_MESSAGE = "Curation created, but you can't add drops with that group.";
const CREATE_AND_ADD_FALLBACK_MESSAGE = "Curation created, but the drop could not be added. Please try again.";
const AUTO_CLOSE_DELAY_MS = 350;
const getCreateAndAddErrorMessage = (error: unknown): string => {
  const fallbackErrorMessage = "Failed to add drop to curation.";
  const errorMessage = getErrorMessage(error, fallbackErrorMessage);
  if (isPermissionErrorMessage(errorMessage)) {
    return CREATE_AND_ADD_PERMISSION_MESSAGE;
  }
  if (errorMessage === fallbackErrorMessage) {
    return CREATE_AND_ADD_FALLBACK_MESSAGE;
  }
  return `Curation created, but the drop could not be added: ${errorMessage}`;
};
const sortCurationsForDialog = (left: DropCurationMembership, right: DropCurationMembership): number => {
  if (left.drop_included !== right.drop_included) {
    return Number(right.drop_included) - Number(left.drop_included);
  }
  return left.name.localeCompare(right.name);
};
function MembershipActionButton(t0) {
  const $ = _c(13);
  const {
    action,
    disabled,
    loading,
    onClick
  } = t0;
  const isAdd = action === "add";
  const t1 = disabled || loading;
  const t2 = disabled || loading ? undefined : onClick;
  const t3 = isAdd ? "tw-border-white tw-bg-iron-100 tw-text-iron-950 tw-shadow-[0_0_10px_rgba(255,255,255,0.2)] desktop-hover:hover:-tw-translate-y-0.5 desktop-hover:hover:tw-bg-iron-50 desktop-hover:hover:tw-shadow-[0_0_15px_rgba(255,255,255,0.3)]" : "tw-border-white/20 tw-bg-iron-900 tw-text-iron-100 tw-shadow-[0_2px_10px_rgba(0,0,0,0.2)] desktop-hover:hover:tw-border-white/40 desktop-hover:hover:tw-bg-iron-800";
  const t4 = (disabled || loading) && "tw-cursor-not-allowed tw-opacity-50 desktop-hover:hover:tw-translate-y-0 desktop-hover:hover:tw-shadow-none";
  let t5;
  if ($[0] !== t3 || $[1] !== t4) {
    t5 = clsx("tw-inline-flex tw-min-w-[5.5rem] tw-items-center tw-justify-center tw-gap-x-2 tw-rounded-lg tw-border tw-border-solid tw-px-3.5 tw-py-2 tw-text-xs tw-font-semibold tw-transition-all tw-duration-300 tw-ease-out", t3, t4);
    $[0] = t3;
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== loading) {
    t6 = loading && <Spinner dimension={14} />;
    $[3] = loading;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const t7 = isAdd ? "Add" : "Remove";
  let t8;
  if ($[5] !== t7) {
    t8 = <span>{t7}</span>;
    $[5] = t7;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t5 || $[10] !== t6 || $[11] !== t8) {
    t9 = <button type="button" disabled={t1} onClick={t2} className={t5}>{t6}{t8}</button>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t5;
    $[10] = t6;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  return t9;
}
function CurationMembershipRow(t0) {
  const $ = _c(27);
  const {
    curation,
    disabled,
    loading,
    onUpdateMembership
  } = t0;
  const membershipAction = curation.drop_included ? "remove" : "add";
  const t1 = curation.drop_included ? "tw-border-white/30 tw-bg-iron-800 tw-p-3.5 tw-shadow-[0_4px_15px_rgba(0,0,0,0.4)]" : "tw-border-white/[0.06] tw-bg-iron-950 tw-p-3.5 desktop-hover:hover:tw-border-white/10 desktop-hover:hover:tw-bg-iron-900/60";
  let t2;
  if ($[0] !== t1) {
    t2 = clsx("tw-group tw-relative tw-flex tw-h-16 tw-items-center tw-justify-between tw-gap-3 tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-transition-all tw-duration-300", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = curation.drop_included ? "tw-text-iron-50" : "tw-text-iron-200 desktop-hover:group-hover:tw-text-iron-50";
  let t4;
  if ($[2] !== t3) {
    t4 = clsx("tw-mb-0 tw-min-w-0 tw-flex-shrink tw-truncate tw-text-md tw-font-semibold tw-leading-tight tw-transition-colors", t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== curation.name || $[5] !== t4) {
    t5 = <p className={t4}>{curation.name}</p>;
    $[4] = curation.name;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = curation.drop_included ? "tw-bg-white/15 tw-opacity-100" : "tw-opacity-0";
  let t7;
  if ($[7] !== t6) {
    t7 = clsx("tw-flex tw-h-4 tw-w-4 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-transition-opacity", t6);
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <svg viewBox="0 0 24 24" fill="none" className="tw-h-2.5 tw-w-2.5 tw-text-iron-100" stroke="currentColor" strokeWidth="4"><polyline points="20 6 9 17 4 12" /></svg>;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t7) {
    t9 = <span className={t7} aria-hidden="true">{t8}</span>;
    $[10] = t7;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t5 || $[13] !== t9) {
    t10 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-nowrap tw-items-center tw-gap-2.5">{t5}{t9}</div>;
    $[12] = t5;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== membershipAction || $[16] !== onUpdateMembership) {
    t11 = () => onUpdateMembership(membershipAction);
    $[15] = membershipAction;
    $[16] = onUpdateMembership;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== disabled || $[19] !== loading || $[20] !== membershipAction || $[21] !== t11) {
    t12 = <MembershipActionButton action={membershipAction} disabled={disabled} loading={loading} onClick={t11} />;
    $[18] = disabled;
    $[19] = loading;
    $[20] = membershipAction;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t10 || $[24] !== t12 || $[25] !== t2) {
    t13 = <div className={t2}>{t10}{t12}</div>;
    $[23] = t10;
    $[24] = t12;
    $[25] = t2;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  return t13;
}
export default function WaveDropCurationsDialog(t0) {
  const $ = _c(53);
  const {
    dropId,
    wave,
    isOpen,
    onClose
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    setToast
  } = useAuth();
  const [isCreateCurationOpen, setIsCreateCurationOpen] = useState(false);
  const autoCloseTimeoutRef = useRef(null);
  let t1;
  if ($[0] !== dropId || $[1] !== isOpen) {
    t1 = {
      dropId,
      enabled: isOpen
    };
    $[0] = dropId;
    $[1] = isOpen;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: t2,
    isLoading,
    isError,
    refetch
  } = useDropCurations(t1);
  let t3;
  if ($[3] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const curations = t3;
  let t4;
  if ($[5] !== dropId) {
    t4 = {
      dropId
    };
    $[5] = dropId;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    updateMembershipAsync,
    isPending,
    pendingCurationId
  } = useDropCurationMembershipMutation(t4);
  let t5;
  if ($[7] !== curations) {
    t5 = curations.filter(_temp).sort(sortCurationsForDialog);
    $[7] = curations;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const sortedCurations = t5;
  const hasVisibleCurations = sortedCurations.length > 0;
  const hasHiddenCurations = curations.length > 0 && !hasVisibleCurations;
  const isFirstCurationEmptyState = !isLoading && !isError && !hasHiddenCurations && !hasVisibleCurations;
  const emptyStateTitle = hasHiddenCurations ? "No curations you can manage" : "No curations yet";
  const emptyStateDescription = hasHiddenCurations ? "Create a new curation for this wave and add this drop to it." : "Create the first curation for this wave and add this drop to it.";
  let t6;
  if ($[9] !== refetch) {
    t6 = async () => {
      await refetch();
    };
    $[9] = refetch;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleRetry = t6;
  let t7;
  if ($[11] !== pathname || $[12] !== router || $[13] !== searchParams) {
    t7 = curationId => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.set("curation", curationId);
      const nextQuery = params.toString();
      const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
      router.replace(nextUrl, {
        scroll: false
      });
    };
    $[11] = pathname;
    $[12] = router;
    $[13] = searchParams;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const navigateToCuration = t7;
  const clearAutoCloseTimeout = () => {
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }
  };
  const scheduleAutoClose = () => {
    clearAutoCloseTimeout();
    autoCloseTimeoutRef.current = globalThis.setTimeout(() => {
      autoCloseTimeoutRef.current = null;
      setIsCreateCurationOpen(false);
      onClose();
    }, AUTO_CLOSE_DELAY_MS);
  };
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [];
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  useEffect(() => () => {
    clearAutoCloseTimeout();
  }, t8);
  const handleClose = () => {
    clearAutoCloseTimeout();
    setIsCreateCurationOpen(false);
    onClose();
  };
  const handleUpdateMembership = async (curationId_0, action) => {
    try {
      await updateMembershipAsync(curationId_0, action);
      scheduleAutoClose();
    } catch {
      clearAutoCloseTimeout();
    }
  };
  let t9;
  if ($[16] !== handleClose || $[17] !== navigateToCuration || $[18] !== refetch || $[19] !== setToast || $[20] !== updateMembershipAsync) {
    t9 = async curation_0 => {
      const refreshResult = await refetch();
      const createdCuration = refreshResult.isError ? undefined : refreshResult.data?.find(item => item.id === curation_0.id);
      if (createdCuration?.authenticated_user_can_curate === false) {
        setToast({
          type: "warning",
          message: CREATE_AND_ADD_PERMISSION_MESSAGE
        });
        return;
      }
      ;
      try {
        await updateMembershipAsync(curation_0.id, "add", {
          suppressToast: true
        });
        setToast({
          type: "success",
          message: "Curation created and drop added."
        });
        handleClose();
        navigateToCuration(curation_0.id);
      } catch (t10) {
        const error = t10;
        setToast({
          type: "error",
          message: getCreateAndAddErrorMessage(error)
        });
      }
    };
    $[16] = handleClose;
    $[17] = navigateToCuration;
    $[18] = refetch;
    $[19] = setToast;
    $[20] = updateMembershipAsync;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const handleCreatedFromCurate = t9;
  const T0 = MobileWrapperDialog;
  const t10 = "Curate";
  const t11 = true;
  const t12 = true;
  const t13 = true;
  const t14 = "md:tw-max-w-lg";
  const t15 = "tw-mb-4 tw-border-b tw-border-solid tw-border-x-0 tw-border-t-0 tw-border-iron-800 tw-pb-3 tw-pt-6";
  const t16 = "-tw-translate-x-0.5 tw-translate-y-2 tw-z-10 tw-bg-transparent tw-text-white tw-shadow-lg";
  const t17 = "tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-pb-2";
  const t18 = "tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300";
  const t19 = "tw-flex tw-flex-col tw-gap-6 tw-px-4 tw-pb-6 sm:tw-px-6";
  let t20;
  if ($[22] !== hasVisibleCurations || $[23] !== isFirstCurationEmptyState) {
    t20 = !isFirstCurationEmptyState && <div className="tw-flex tw-flex-col tw-gap-4 tw-pt-2 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between"><p className="tw-mb-0 tw-text-sm tw-text-iron-400 sm:tw-flex-1 sm:tw-pr-4">Add or remove this drop from curations in this wave.</p>{hasVisibleCurations && <div className="tw-flex tw-flex-shrink-0"><SecondaryButton onClicked={() => setIsCreateCurationOpen(true)} size="sm" className="!tw-text-xs">Create and add curation</SecondaryButton></div>}</div>;
    $[22] = hasVisibleCurations;
    $[23] = isFirstCurationEmptyState;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  let t21;
  if ($[25] !== isLoading) {
    t21 = isLoading && <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-py-16"><div className="tw-flex tw-items-center tw-gap-3 tw-text-sm tw-font-medium tw-text-iron-400"><Spinner dimension={18} /><span>Loading curations...</span></div></div>;
    $[25] = isLoading;
    $[26] = t21;
  } else {
    t21 = $[26];
  }
  let t22;
  if ($[27] !== handleRetry || $[28] !== isError || $[29] !== isLoading) {
    t22 = !isLoading && isError && <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-white/[0.06] tw-bg-iron-900 tw-p-6 tw-text-center tw-shadow-sm"><p className="tw-mb-4 tw-text-sm tw-font-medium tw-text-iron-300">Unable to load curations for this drop.</p><div className="tw-flex tw-justify-center"><SecondaryButton onClicked={handleRetry} size="sm" className="!tw-text-xs">Try again</SecondaryButton></div></div>;
    $[27] = handleRetry;
    $[28] = isError;
    $[29] = isLoading;
    $[30] = t22;
  } else {
    t22 = $[30];
  }
  let t23;
  if ($[31] !== emptyStateDescription || $[32] !== emptyStateTitle || $[33] !== isError || $[34] !== isLoading || $[35] !== sortedCurations.length) {
    t23 = !isLoading && !isError && sortedCurations.length === 0 && <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-white/[0.06] tw-bg-iron-900 tw-p-8 tw-text-center tw-shadow-sm"><p className="tw-mb-2 tw-text-base tw-font-semibold tw-text-iron-100">{emptyStateTitle}</p><p className="tw-mx-auto tw-mb-6 tw-max-w-sm tw-text-sm tw-text-iron-400">{emptyStateDescription}</p><div className="tw-flex tw-justify-center"><PrimaryButton loading={false} disabled={false} onClicked={() => setIsCreateCurationOpen(true)} padding="tw-px-4 tw-py-2">Create first curation</PrimaryButton></div></div>;
    $[31] = emptyStateDescription;
    $[32] = emptyStateTitle;
    $[33] = isError;
    $[34] = isLoading;
    $[35] = sortedCurations.length;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  const t24 = !isLoading && !isError && sortedCurations.length > 0 && <div className="tw-flex tw-flex-col tw-gap-2">{sortedCurations.map(curation_1 => <CurationMembershipRow key={curation_1.id} curation={curation_1} disabled={isPending} loading={pendingCurationId === curation_1.id} onUpdateMembership={async action_0 => await handleUpdateMembership(curation_1.id, action_0)} />)}</div>;
  let t25;
  if ($[37] !== t20 || $[38] !== t21 || $[39] !== t22 || $[40] !== t23 || $[41] !== t24) {
    t25 = <div className={t17}><div className={t18}><div className={t19}>{t20}{t21}{t22}{t23}{t24}</div></div></div>;
    $[37] = t20;
    $[38] = t21;
    $[39] = t22;
    $[40] = t23;
    $[41] = t24;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  let t26;
  if ($[43] !== handleCreatedFromCurate || $[44] !== isCreateCurationOpen || $[45] !== wave) {
    t26 = isCreateCurationOpen && <MyStreamWaveCurationCreateDialog wave={wave} isOpen={isCreateCurationOpen} onClose={() => setIsCreateCurationOpen(false)} showSuccessToast={false} onSaved={handleCreatedFromCurate} />;
    $[43] = handleCreatedFromCurate;
    $[44] = isCreateCurationOpen;
    $[45] = wave;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  let t27;
  if ($[47] !== T0 || $[48] !== handleClose || $[49] !== isOpen || $[50] !== t25 || $[51] !== t26) {
    t27 = <T0 title={t10} isOpen={isOpen} onClose={handleClose} noPadding={t11} tabletModal={t12} tall={t13} maxWidthClass={t14} headerClassName={t15} mobileCloseButtonClassName={t16}>{t25}{t26}</T0>;
    $[47] = T0;
    $[48] = handleClose;
    $[49] = isOpen;
    $[50] = t25;
    $[51] = t26;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  return t27;
}
function _temp(curation) {
  return curation.authenticated_user_can_curate === true;
}